import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'
import { rememberTraceId } from '../utils/traceContext'

export default defineNuxtPlugin(() => {
  const { public: config } = useRuntimeConfig()

  if (!config.otelEnabled || !config.otelCollectorUrl || config.isTest) {
    return
  }

  // Tracing is observability infrastructure — it must never crash app startup.
  // Any init failure (bad URL, network, version mismatch) is swallowed here.
  try {
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: String(config.otelServiceName),
      [ATTR_SERVICE_VERSION]: String(config.appVersion ?? 'unknown'),
    })

    // Trim a trailing slash so a configured "http://host:4318/" doesn't become
    // "http://host:4318//v1/traces" (which collectors reject).
    const collectorUrl = String(config.otelCollectorUrl).replace(/\/$/, '')
    const exporter = new OTLPTraceExporter({
      url: `${collectorUrl}/v1/traces`,
    })

    const provider = new WebTracerProvider({
      resource,
      spanProcessors: [new BatchSpanProcessor(exporter)],
    })
    provider.register()

    // Distributed tracing: inject W3C trace headers (traceparent/tracestate)
    // into requests to our own backends so frontend spans link to backend traces.
    //
    // This is OFF by default because adding these headers to a cross-origin
    // request triggers a CORS preflight, which the backend only passes if it
    // lists `traceparent`/`tracestate` in Access-Control-Allow-Headers. Enabling
    // it before the backend allows those headers breaks every API call.
    // Turn on with OTEL_PROPAGATE_HEADERS=true only once the backend CORS is ready.
    const backendUrls = [
      config.processingUrl,
      config.resourceCreationUrl,
      config.documentsUrl,
      config.grammaticalUrl,
      config.glossariesUrl,
      config.statisticsUrl,
      config.authUrl,
      config.vectorSearchUrl,
      config.dictionariesUrl,
    ]
    const propagateTraceHeaderCorsUrls = config.otelPropagateHeaders
      ? backendUrls
          .filter(
            (url): url is string => typeof url === 'string' && url.length > 0
          )
          .map((url) => new RegExp(url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
      : []

    registerInstrumentations({
      instrumentations: [
        getWebAutoInstrumentations({
          '@opentelemetry/instrumentation-document-load': {},
          '@opentelemetry/instrumentation-fetch': {
            propagateTraceHeaderCorsUrls,
            // Remember the trace-id the frontend generated for each request so
            // error handlers can show it when a backend call fails. `result` is
            // the resolved Response (even for 4xx/5xx) or a fetch error.
            applyCustomAttributesOnSpan(span, _request, result) {
              if (result instanceof Response) {
                rememberTraceId(result, span.spanContext().traceId)
              }
            },
          },
          '@opentelemetry/instrumentation-xml-http-request': {
            propagateTraceHeaderCorsUrls,
          },
          '@opentelemetry/instrumentation-user-interaction': {},
        }),
      ],
    })
  } catch (e) {
    console.warn('[otel] failed to initialise, tracing disabled:', e)
  }
})
